import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import { useUpdateJobTagsMutation } from 'containers/jobs/api';
import { Tag } from 'containers/tags/tags.model';
import { useParams } from 'react-router-dom';
import { useContext, useState } from 'react';
import TagPill from 'containers/tags/create-tag/Tag';
import { getSortedJobTags } from 'containers/jobs/utils';
import { selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { JobPermissionsContext } from '../JobDetails';
import JobTagListModal from './JobTagListModal';
import CreateTagModal from 'containers/tags/components/CreateTagModal';

interface JobTagsManagerProps {
  tags: Tag[];
  jobTags: Tag[];
  jobNumber: string;
  isLoadingTags?: boolean;
}

const JobTagsManager = ({ tags, jobTags, jobNumber, isLoadingTags = false }: JobTagsManagerProps) => {
  const { teamId, rootJobId } = useParams();
  const { canEditJobs } = useContext(JobPermissionsContext);
  const currentJobSettingsPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { editJobSettings } = currentJobSettingsPermissions ?? {};

  const [updateJobTags, { isLoading: isUpdatingJobTags }] = useUpdateJobTagsMutation();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);

  const handleRemoveTag = async (tagId: string) => {
    await updateJobTags({
      teamId: Number(teamId),
      jobId: rootJobId,
      body: { tagIds: jobTags.map(tag => tag.id).filter(id => id !== tagId) }
    });
  };

  const handleSaveTags = async (selectedTags: Tag[]) => {
    await updateJobTags({
      teamId: Number(teamId),
      jobId: rootJobId,
      body: { tagIds: selectedTags.map(tag => tag.id) }
    });
    setIsTagModalOpen(false);
  };

  const handleCreateTag = (tag: Tag) => {
    // Add the new tag to the job tags
    handleSaveTags([...jobTags, tag]);
    setIsCreateTagModalOpen(false);
  };

  const handleOpenCreateTagModal = () => {
    setIsTagModalOpen(false);
    setIsCreateTagModalOpen(true);
  };

  return (
    <>
      {getSortedJobTags(jobTags).map((tag, index) => (
        <TagPill
          key={tag.id}
          id={`tag-${index + 1}`}
          tag={tag}
          showCloseButton
          handleRemoveTag={handleRemoveTag}
          isDisabled={isUpdatingJobTags}
          className="flex items-center rounded-[4px] px-1.5 py-1"
          textStyle="text-sbase font-semibold"
        />
      ))}
      {getSortedJobTags(jobTags).length === 0 && !canEditJobs && <div className="text-sbase text-primaryText">-</div>}
      {canEditJobs && (
        <button
          className="flex h-[28px] w-[87px] shrink-0 items-center gap-2 text-sbase font-semibold text-secondary"
          onClick={() => setIsTagModalOpen(true)}>
          <LineItemPlusReactIcon id="job-details-add-tag-button" className="h-5 w-5 shrink-0" />
          Add tag
        </button>
      )}

      <JobTagListModal
        open={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        selectedTags={jobTags}
        setSelectedTags={handleSaveTags}
        allTags={tags ?? []}
        isLoading={isLoadingTags}
        jobNumber={jobNumber}
        canCreateTag={!!editJobSettings}
        onCreateTag={handleOpenCreateTagModal}
        isSaving={isUpdatingJobTags}
      />

      <CreateTagModal
        isOpen={isCreateTagModalOpen}
        onClose={() => setIsCreateTagModalOpen(false)}
        onCreate={handleCreateTag}
      />
    </>
  );
};

export default JobTagsManager;
